import * as React from 'react'

import { Logo } from '@/components/brand/Logo'
import { Anchor } from '@/components/ui/Anchor'
import { cn } from '@/lib/utils'
import { EstimateBanner } from './EstimateBanner'
import { FooterEdgeLine } from './FooterEdgeLine'

export interface FooterLink {
  label: string
  /** Omit to render this item inert (e.g. "coming soon" — nothing to link to yet). */
  href?: string
  /** Opens the link in a new tab — use for destinations outside this app. */
  external?: boolean
  /** Short badge, e.g. "Coming soon". */
  tag?: string
}

export interface FooterLinkColumn {
  heading: string
  links: FooterLink[]
}

export interface SiteFooterEstimateBanner {
  title?: string
  description: React.ReactNode
  buttonLabel: string
  buttonLabelMobile?: string
  inputPlaceholder?: string
  onSubmit?: (value: string) => void
}

export interface SiteFooterProps {
  tagline: string
  linkColumns: FooterLinkColumn[]
  legalLinks: { label: string; href: string }[]
  /** Defaults to the current year and Cai's legal entity name if omitted. */
  copyright?: string
  /** Fine-print compliance copy, e.g. licensing disclosure. Accepts multiple paragraphs. Optional. */
  note?: React.ReactNode
  /** Permanent inline copy of the estimate CTA — pairs with the floating banner that dismisses once the footer scrolls into view. */
  estimateBanner?: SiteFooterEstimateBanner
  /** The reflective-tape edge lines pair with matching ones on the rest of the page (see MarketingLayout's `EdgeLine`) — set this on pages that don't render those, so the footer isn't the only place they show up. */
  hideEdgeLines?: boolean
  className?: string
}

const FOOTER_LINK_CLASSES = 'flex items-center gap-2 text-inherit no-underline opacity-85 max-sm:flex-wrap'

function FooterLinkTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-ink-400 rounded-full border border-white/20 px-1.5 py-px font-mono text-[10px] tracking-[0.08em] uppercase">
      {children}
    </span>
  )
}

function FooterLinkItem({ label, href, external, tag }: FooterLink) {
  if (!href) {
    return (
      <span className={cn(FOOTER_LINK_CLASSES, 'cursor-default opacity-50')}>
        {label}
        {tag ? <FooterLinkTag>{tag}</FooterLinkTag> : null}
      </span>
    )
  }

  return (
    <Anchor
      className={cn(FOOTER_LINK_CLASSES, 'hover:opacity-100')}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
    >
      {label}
      {tag ? <FooterLinkTag>{tag}</FooterLinkTag> : null}
    </Anchor>
  )
}

/**
 * Always the dark, grounding treatment — there is no light footer variant
 * (an earlier light/white footer was removed from the reference; don't
 * reintroduce it without an explicit design decision).
 */
export function SiteFooter({
  tagline,
  linkColumns,
  legalLinks,
  copyright,
  note,
  estimateBanner,
  hideEdgeLines,
  className,
}: SiteFooterProps) {
  const copyrightText = copyright ?? `© ${new Date().getFullYear()} Cai Labs, Inc. All rights reserved.`
  return (
    <footer
      data-site-footer
      className={cn('bg-ink-900 relative px-5.5 pt-24 pb-12 text-sm text-white/70', className)}
    >
      {hideEdgeLines ? null : (
        <>
          <FooterEdgeLine side="left" />
          <FooterEdgeLine side="right" />
        </>
      )}
      <div className="mx-auto max-w-(--maxw)">
        <div className="flex flex-wrap items-start gap-16 pb-20">
          <div className="flex max-w-55 flex-col gap-4">
            <Logo theme="white" size="small" width={64} aria-label="Cai" />
            <p className="text-[13px] leading-normal opacity-60">{tagline}</p>
          </div>
          <div className="ml-auto flex flex-wrap gap-16 max-sm:ml-0 max-sm:flex-nowrap max-sm:gap-4">
            {linkColumns.map((column) => (
              <div key={column.heading} className="flex min-w-35 flex-col gap-4 max-sm:min-w-0 max-sm:flex-1">
                <span className="mb-4 font-mono text-xs tracking-[0.08em] uppercase opacity-50">
                  {column.heading}
                </span>
                {column.links.map((link) => (
                  <FooterLinkItem key={link.label} {...link} />
                ))}
              </div>
            ))}
          </div>
        </div>
        {estimateBanner ? <EstimateBanner inline {...estimateBanner} /> : null}
        <div className="border-ink-700 flex flex-wrap justify-between gap-x-6 gap-y-2 border-t pt-6 font-mono text-xs opacity-80">
          <span>{copyrightText}</span>
          <span className="flex gap-6">
            {legalLinks.map((link) => (
              <Anchor key={link.label} href={link.href} className="text-inherit no-underline opacity-85">
                {link.label}
              </Anchor>
            ))}
          </span>
        </div>
        {note ? (
          <div className="[&_a:hover]:text-brand-600 mt-8 flex flex-col gap-4 font-mono text-xs leading-normal text-white/60 [&_a]:text-white [&_a]:underline [&_a]:underline-offset-2">
            {note}
          </div>
        ) : null}
      </div>
    </footer>
  )
}
